import {getCoreRowModel, getExpandedRowModel, useReactTable as useReactTableLibrary} from '@tanstack/react-table'
import {useCallback, useMemo} from 'react'

import {MIN_CELL_WIDTH} from '../../components/table/constants'
import type {TableDataType} from '../../components/table/table-data-type'
import {isRowSelectable} from '../../components/table/use-row-selection'
import {useTableHierarchy} from '../../features/sub-issues/use-table-hierarchy'
import type {ColumnModel} from '../../models/column-model'
import {useMemexItems} from '../../state-providers/memex-items/use-memex-items'
import {getRowId} from './helpers/get-row-id'
import type {ColumnDef, Row} from './helpers/types'
import {useColumnSizing} from './hooks/use-column-sizing'
import {useColumnSizingInfo} from './hooks/use-column-sizing-info'
import {controlledStateErrorWarnings, useControlledTableState} from './hooks/use-controlled-table-state'
import {RowDraggerColumnDef} from './providers/columns/row-dragger-column-def'
import {useTableColumns} from './providers/columns/use-table-columns'
import {getCustomGroupedRowModel} from './row-models/get-custom-grouped-row-model'

type TableProps = {
  fields: ReadonlyArray<ColumnModel>
  pinnedRightColumnDefs?: ReadonlyArray<ColumnDef<TableDataType>>
}

export function useReactTable({fields, pinnedRightColumnDefs}: TableProps) {
  return useTanStackReactTable({fields, pinnedRightColumnDefs})
}

const useTanStackReactTable = ({
  fields,
  pinnedRightColumnDefs,
}: Pick<TableProps, 'fields' | 'pinnedRightColumnDefs'>) => {
  const readonlyData = useReactTableData()
  // Replace with mutable array while maintaining referential equality checks
  const data = useMemo(() => [...readonlyData], [readonlyData])
  const {columnDefinitions, columnVisibility} = useTableColumns()
  const {columnSizingInfo, onColumnSizingInfoChange} = useColumnSizingInfo()
  const {columnSizing, onColumnSizingChange} = useColumnSizing()
  const {hierarchyEnabled} = useTableHierarchy()

  const pinnedLeftColumnDefs: ReadonlyArray<ColumnDef<TableDataType>> = useMemo(() => [RowDraggerColumnDef], [])

  const allColumnDefs = useMemo(
    () => [...pinnedLeftColumnDefs, ...columnDefinitions, ...(pinnedRightColumnDefs ?? [])],
    [columnDefinitions, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const {expanded, grouping, groupQueryData, sorting, subIssuesQueryData} = useControlledTableState()
  const {copilotTasksEnabled} = subIssuesQueryData
  const columnOrder = useMemo(
    () => [
      ...pinnedLeftColumnDefs.map(def => def.id),
      ...fields.map(f => f.id.toString()),
      ...(pinnedRightColumnDefs?.map(def => def.id) ?? []),
    ],
    [fields, pinnedLeftColumnDefs, pinnedRightColumnDefs],
  )

  const getRowCanExpand = useCallback(
    (row: Row<TableDataType>) => {
      if (row.getIsGrouped()) return true
      if (!hierarchyEnabled && !copilotTasksEnabled) return false
      if (hierarchyEnabled) {
        const subIssuesProgress = row.original.getSubIssuesProgress()
        if (subIssuesProgress && subIssuesProgress.total > 0) {
          return true
        }
      }
      if (copilotTasksEnabled && row.subRows.some(r => r.agentAssignmentTaskData)) {
        return true
      }
      return false
    },
    [hierarchyEnabled, copilotTasksEnabled],
  )

  return useReactTableLibrary<TableDataType>({
    columns: allColumnDefs,
    data,
    getRowId,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: getCustomGroupedRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getRowCanExpand: hierarchyEnabled || copilotTasksEnabled ? getRowCanExpand : undefined,
    enableRowSelection: isRowSelectable,
    manualSorting: true,
    columnResizeMode: 'onChange',
    defaultColumn: {minSize: MIN_CELL_WIDTH},
    autoResetAll: false,
    groupedColumnMode: false,
    initialState: {
      columnPinning: {
        left: pinnedLeftColumnDefs?.map(def => def.id),
        right: pinnedRightColumnDefs?.map(def => def.id),
      },
    },
    state: {
      columnVisibility,
      columnOrder,
      columnSizing,
      columnSizingInfo,
      expanded,
      grouping,
      groupQueryData,
      sorting,
      subIssuesQueryData,
    },
    onColumnSizingChange,
    onColumnSizingInfoChange,
    ...controlledStateErrorWarnings,
  })
}

function useReactTableData() {
  const {items} = useMemexItems()
  return useMemo(() => items, [items])
}
